import React, { useState, useEffect, useCallback } from 'react';
import { Card, List, Space, Button, Badge, Popconfirm, Empty, Spin, message, Collapse, Tag } from 'antd';
import { DeleteOutlined, FileOutlined, FolderOutlined } from '@ant-design/icons';
import { 
  DOCUMENT_GROUPS,
  getDocumentTypeById, 
  getDocumentGroupById,
  getFileTypeIcon, 
  formatFileSize 
} from '../config/documentTypes';
import apiService from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const { Panel } = Collapse;

const DocumentList = ({ 
  projectId = null, 
  refresh = 0, 
  onDocumentDeleted, 
  className = '' 
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const { getAuthToken } = useAuth();

  // Load documents
  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getAuthToken();
      const response = await apiService.getDocuments(token, projectId);
      setDocuments(response.documents || []);
    } catch (err) {
      console.error('Failed to load documents:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, getAuthToken]);

  // Delete document
  const handleDelete = useCallback(async (documentId, filename) => {
    setDeletingIds(prev => new Set([...prev, documentId]));
    
    try {
      const token = getAuthToken();
      await apiService.deleteDocument(documentId, token);
      
      message.success(`"${filename}" deleted successfully!`);
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      
      if (onDocumentDeleted) {
        onDocumentDeleted(documentId);
      }
    } catch (err) {
      console.error('Failed to delete document:', err);
      message.error(`Failed to delete document: ${err.message}`);
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  }, [getAuthToken, onDocumentDeleted]);

  // Group documents by type and then by group
  const groupedDocuments = React.useMemo(() => {
    const groups = {};
    
    documents.forEach(doc => {
      const typeId = doc.document_type || null;
      const documentType = getDocumentTypeById(typeId);
      const groupId = documentType?.group || 'general';
      
      if (!groups[groupId]) {
        groups[groupId] = {
          group: getDocumentGroupById(groupId),
          types: {}
        };
      }
      
      if (!groups[groupId].types[typeId || 'general']) {
        groups[groupId].types[typeId || 'general'] = {
          type: documentType,
          documents: []
        };
      }
      
      groups[groupId].types[typeId || 'general'].documents.push(doc);
    });
    
    return groups;
  }, [documents]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments, refresh]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading documents...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ color: '#ff4d4f', marginBottom: 16 }}>⚠️ Error loading documents</div>
          <div>{error}</div>
          <Button 
            type="primary" 
            onClick={loadDocuments}
            style={{ marginTop: 16 }}
          >
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  if (documents.length === 0) {
    return (
      <Card>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No documents uploaded yet"
        >
          <div style={{ color: '#6b7280', fontSize: '14px', marginTop: 8 }}>
            Upload your first document to get started!
          </div>
        </Empty>
      </Card>
    );
  }

  const renderDocument = (doc) => {
    const documentType = getDocumentTypeById(doc.document_type || null);
    const isDeleting = deletingIds.has(doc.id);
    
    return (
      <List.Item
        key={doc.id}
        className="document-item"
        actions={[
          <Popconfirm
            title="Delete Document"
            description={`Are you sure you want to delete "${doc.filename}"?`}
            onConfirm={() => handleDelete(doc.id, doc.filename)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
              loading={isDeleting}
              disabled={isDeleting}
            >
              Delete
            </Button>
          </Popconfirm>
        ]}
      >
        <List.Item.Meta
          avatar={
            <div style={{ fontSize: '24px' }}>
              {getFileTypeIcon(doc.file_type)}
            </div>
          }
          title={
            <div>
              <span>{doc.filename}</span>
              {documentType && documentType.id && (
                <Tag 
                  color={getDocumentGroupById(documentType.group)?.color}
                  style={{ marginLeft: 8, fontSize: '11px' }}
                >
                  {documentType.label}
                </Tag>
              )}
            </div>
          }
          description={
            <Space size={12} wrap>
              <span>{doc.file_type?.toUpperCase()}</span>
              <span>{formatFileSize(doc.file_size)}</span>
              <span>{doc.created_at ? new Date(doc.created_at * 1000).toLocaleDateString() : 'N/A'}</span>
            </Space>
          }
        />
      </List.Item>
    );
  };

  const renderDocumentTypeSection = (typeData, typeId, showTypeHeader = true) => {
    const { type, documents: typeDocs } = typeData;
    
    return (
      <div key={typeId} style={{ marginBottom: 16 }}>
        {showTypeHeader && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: 12,
            padding: '8px 12px',
            backgroundColor: '#fafafa',
            borderRadius: 6,
            border: '1px solid #f0f0f0'
          }}>
            <span style={{ fontSize: '16px', marginRight: 8 }}>
              {type?.icon || '📄'}
            </span>
            <span style={{ fontWeight: 500, color: '#000000' }}>
              {type?.label || 'General Documents'}
            </span>
            <Badge 
              count={typeDocs.length} 
              style={{ marginLeft: 8 }}
              showZero
            />
          </div>
        )}
        
        <List
          size="small"
          dataSource={typeDocs}
          renderItem={renderDocument}
        />
      </div>
    );
  };

  const renderGroup = (groupData, groupId) => {
    const { group, types } = groupData;
    const totalDocs = Object.values(types).reduce((sum, typeData) => sum + typeData.documents.length, 0);
    
    return (
      <Panel 
        key={groupId}
        header={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <span 
                style={{ 
                  fontSize: '20px', 
                  marginRight: 12,
                  color: group?.color || '#8b5cf6'
                }}
              >
                {group?.icon || '📄'}
              </span>
              <div>
                <div style={{ fontWeight: 600, color: '#000000' }}>
                  {group?.label || 'General Documents'}
                </div>
                <div style={{ fontSize: '12px', color: '#404040' }}>
                  {group?.description || 'Miscellaneous documents'}
                </div>
              </div>
            </div>
            <Badge 
              count={totalDocs} 
              showZero
            />
          </div>
        }
        style={{
          marginBottom: 8,
          border: `1px solid ${group?.color}40`,
        }}
      >
        {Object.entries(types).map(([typeId, typeData]) => {
          // Hide type header if there's only one type or if type label matches group label
          const shouldShowTypeHeader = Object.keys(types).length > 1 && 
            typeData.type?.label !== (group?.label || 'General Documents');
          return renderDocumentTypeSection(typeData, typeId, shouldShowTypeHeader);
        })}
      </Panel>
    );
  };

  return (
    <div className={`grouped-document-list ${className}`}>
      <Card 
        title={
          <Space>
            <FolderOutlined />
            <span>Documents</span>
            <Badge 
              count={documents.length} 
              style={{ backgroundColor: '#8b5cf6' }}
              showZero
            />
          </Space>
        }
        size="small"
      >
        <Collapse 
          defaultActiveKey={Object.keys(groupedDocuments)}
          ghost
        >
          {Object.entries(groupedDocuments).map(([groupId, groupData]) => 
            renderGroup(groupData, groupId)
          )}
        </Collapse>
      </Card>
    </div>
  );
};

export default DocumentList;